import { BillableMatterListItem } from 'types/billable-matters';
import { CloseOnSendDto, UpdateCloseOnSendDto } from 'types/caseClosing';
import { ExpenseCreateDto } from 'types/expenses';
import {
    InvoiceExpense,
    InvoiceExpenseBulkUpdateDto,
    InvoiceExpenseUpdateDto,
    InvoiceMatterAdjustmentDto,
    InvoiceMatterBulkDiscountDto,
    InvoiceMatterStatusUpdateDto,
    InvoiceMatterSummary,
    InvoiceMatterUpdateDto,
    InvoiceTimecard,
    InvoiceTimecardBulkUpdateDto,
    InvoiceTimecardsAndExpensesDto,
    InvoiceTimecardUpdateDto,
    LimitType,
} from 'types/invoice-details';
import { NamedEntityMinDto } from 'types/other';
import { TimecardCreateUpdateDto } from 'types/timecards';
import { APIExpensesSlice } from './expenses';
import { APIInvoicesSlice } from './invoices';
import { APIMattersSlice } from './matters';
import { portalAPI } from './portalAPI';
import { APITimecardsSlice } from './timecards';

export const APIInvoiceDetailsSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['InvoiceMatters', 'InvoiceTotals'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getInvoiceTimecardsAndExpenses: builder.query<InvoiceTimecardsAndExpensesDto, number>({
                query: (invoiceId) => `/v1/invoice-details/${invoiceId}`,
                keepUnusedDataFor: 0,
                providesTags: (_1, _2, invoiceId) => [{ type: 'InvoiceTotals', id: invoiceId }],
            }),
            getInvoiceMatterSummary: builder.query<InvoiceMatterSummary, { invoiceId: number; matterId: number }>({
                query: ({ invoiceId, matterId }) => `/v1/invoice-details/${invoiceId}/matters/${matterId}`,
                providesTags: (_1, _2, arg) => [{ type: 'InvoiceMatters', invoiceMatter: arg }],
            }),
            updateInvoiceMatter: builder.mutation<void, InvoiceMatterUpdateDto>({
                query: ({ invoiceId, matterId, ...body }) => ({
                    url: `/v1/invoice-details/${invoiceId}/matters/${matterId}`,
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            updateInvoiceMatterAdjustments: builder.mutation<void, InvoiceMatterAdjustmentDto>({
                query: ({ invoiceId, matterId, ...body }) => ({
                    url: `/v1/invoice-details/${invoiceId}/matters/${matterId}/adjustments`,
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            updateInvoiceMatterCloseOnSend: builder.mutation<
                CloseOnSendDto,
                { invoiceId: number; matterId: number } & UpdateCloseOnSendDto
            >({
                query: ({ invoiceId, matterId, ...body }) => ({
                    url: `/v1/invoice-details/${invoiceId}/matters/${matterId}/close-on-send`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            applyBulkDiscount: builder.mutation<void, InvoiceMatterBulkDiscountDto>({
                query: ({ invoiceId, ...body }) => ({
                    url: `/v1/invoice-details/${invoiceId}/matters/bulk-discount`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['InvoiceMatters'],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            updateInvoiceMatterStatus: builder.mutation<void, InvoiceMatterStatusUpdateDto>({
                query: ({ invoiceId, matterId, ...body }) => ({
                    url: `/v1/invoice-details/${invoiceId}/matters/${matterId}/update-status`,
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                            'Notifications',
                        ])
                    );
                },
            }),
            updateInvoiceMatterFeeArrangementType: builder.mutation<void, { invoiceId: number; matterId: number }>({
                query: ({ invoiceId, matterId }) => ({
                    url: `/v1/invoice-details/${invoiceId}/matters/${matterId}/update-fee-arrangement-type`,
                    method: 'PUT',
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            addInvoiceTimecard: builder.mutation<
                InvoiceTimecard,
                { invoiceId: number; matterId: number; dto: Partial<TimecardCreateUpdateDto> }
            >({
                query: ({ invoiceId, dto }) => ({
                    url: `/v1/invoice-details/${invoiceId}/timecards`,
                    method: 'POST',
                    body: dto,
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(APITimecardsSlice.util.invalidateTags(['Timecards', 'TimeRecords']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            addMultiAuthorInvoiceTimecards: builder.mutation<
                InvoiceTimecard[],
                {
                    invoiceId: number;
                    matterId: number;
                    dto: TimecardCreateUpdateDto & { employees: NamedEntityMinDto[] };
                }
            >({
                query: ({ invoiceId, dto }) => ({
                    url: `/v1/invoice-details/${invoiceId}/timecards/multi-author`,
                    method: 'POST',
                    body: dto,
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(APITimecardsSlice.util.invalidateTags(['Timecards', 'TimeRecords']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            copyInvoiceTimecard: builder.mutation<InvoiceTimecard, { invoiceId: number; matterId: number; id: number }>(
                {
                    query: ({ invoiceId, id }) => ({
                        url: `/v1/invoice-details/${invoiceId}/timecards/${id}/copy`,
                        method: 'POST',
                    }),
                    invalidatesTags: (_1, _2, arg) => [
                        { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                    ],
                    async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                        await queryFulfilled;
                        dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                        dispatch(APITimecardsSlice.util.invalidateTags(['Timecards', 'TimeRecords']));
                        dispatch(
                            APIInvoicesSlice.util.invalidateTags([
                                { type: 'InvoiceTotals', id: arg.invoiceId },
                                { type: 'Invoices', id: arg.invoiceId },
                                'InvoicesListItem',
                            ])
                        );
                    },
                }
            ),
            addInvoiceExpense: builder.mutation<
                InvoiceExpense,
                { invoiceId: number; matterId: number; dto: ExpenseCreateDto }
            >({
                query: ({ invoiceId, dto }) => ({
                    url: `/v1/invoice-details/${invoiceId}/expenses`,
                    method: 'POST',
                    body: dto,
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(APIExpensesSlice.util.invalidateTags(['Expenses']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            copyInvoiceExpense: builder.mutation<InvoiceExpense, { invoiceId: number; matterId: number; id: number }>({
                query: ({ invoiceId, id }) => ({
                    url: `/v1/invoice-details/${invoiceId}/expenses/${id}/copy`,
                    method: 'POST',
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(APIExpensesSlice.util.invalidateTags(['Expenses']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            updateInvoiceTimecard: builder.mutation<
                InvoiceTimecard,
                InvoiceTimecardUpdateDto & { skipButtonDisable?: boolean }
            >({
                query: ({ invoiceId, id, skipButtonDisable, ...body }) => ({
                    url: `/v1/invoice-details/${invoiceId}/timecards/${id}`,
                    method: 'PUT',
                    body,
                    _skipButtonDisable: skipButtonDisable,
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matter?.id } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            updateInvoiceExpense: builder.mutation<InvoiceExpense, InvoiceExpenseUpdateDto>({
                query: ({ invoiceId, id, ...body }) => ({
                    url: `/v1/invoice-details/${invoiceId}/expenses/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matter?.id } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            addNewTimecards: builder.mutation<number[], { invoiceId: number; limit: LimitType }>({
                query: ({ invoiceId, limit }) => ({
                    url: `/v1/invoice-details/${invoiceId}/add-new-timecards?limit=${limit}`,
                    method: 'PUT',
                }),
                invalidatesTags: ['InvoiceMatters'],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            addNewExpenses: builder.mutation<number[], { invoiceId: number; limit: LimitType }>({
                query: ({ invoiceId, limit }) => ({
                    url: `/v1/invoice-details/${invoiceId}/add-new-expenses?limit=${limit}`,
                    method: 'PUT',
                }),
                invalidatesTags: ['InvoiceMatters'],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            updateTimecardsHourlyRates: builder.mutation<void, { invoiceId: number }>({
                query: ({ invoiceId }) => ({
                    url: `/v1/invoice-details/${invoiceId}/update-timecards-hourly-rates`,
                    method: 'PUT',
                }),
                invalidatesTags: ['InvoiceMatters'],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            updateInvoiceTimecards: builder.mutation<
                void,
                { invoiceId: number; matterId: number; dtoList: InvoiceTimecardBulkUpdateDto[] }
            >({
                query: ({ invoiceId, matterId, dtoList }) => ({
                    url: `/v1/invoice-details/${invoiceId}/timecards/${matterId}/bulk`,
                    method: 'PUT',
                    body: dtoList,
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            updateInvoiceExpenses: builder.mutation<
                void,
                { invoiceId: number; matterId: number; dtoList: InvoiceExpenseBulkUpdateDto[] }
            >({
                query: ({ invoiceId, matterId, dtoList }) => ({
                    url: `/v1/invoice-details/${invoiceId}/expenses/${matterId}/bulk`,
                    method: 'PUT',
                    body: dtoList,
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            deleteInvoiceTimecards: builder.mutation<
                void,
                { invoiceId: number; matterId: number; timecardIds: number[] }
            >({
                query: ({ invoiceId, matterId, timecardIds }) => ({
                    url: `/v1/invoice-details/${invoiceId}/discard-timecards/${matterId}/bulk`,
                    method: 'DELETE',
                    params: {
                        timecardIds,
                    },
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            deleteInvoiceExpenses: builder.mutation<
                void,
                { invoiceId: number; matterId: number; expenseIds: number[] }
            >({
                query: ({ invoiceId, matterId, expenseIds }) => ({
                    url: `/v1/invoice-details/${invoiceId}/discard-expenses/${matterId}/bulk`,
                    method: 'DELETE',
                    params: {
                        expenseIds,
                    },
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'InvoiceMatters', invoiceMatter: { invoiceId: arg.invoiceId, matterId: arg.matterId } },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            deleteInvoiceMatter: builder.mutation<boolean, { invoiceId: number; matterId: number }>({
                query: ({ invoiceId, matterId }) => ({
                    url: `/v1/invoice-details/${invoiceId}/matters/${matterId}`,
                    method: 'DELETE',
                }),
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
            getAddableMatters: builder.query<BillableMatterListItem[], number>({
                query: (invoiceId) => `/v1/invoice-details/${invoiceId}/addable-matters`,
                keepUnusedDataFor: 0,
            }),
            addInvoiceMatters: builder.mutation<void, { invoiceId: number; matterIds: number[] }>({
                query: ({ invoiceId, matterIds }) => ({
                    url: `/v1/invoice-details/${invoiceId}/matters`,
                    method: 'POST',
                    body: matterIds,
                }),
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            { type: 'InvoiceTotals', id: arg.invoiceId },
                            { type: 'Invoices', id: arg.invoiceId },
                            'InvoicesListItem',
                        ])
                    );
                },
            }),
        }),
        overrideExisting: false,
    });

export const {
    useUpdateInvoiceExpenseMutation,
    useUpdateInvoiceExpensesMutation,
    useUpdateInvoiceTimecardMutation,
    useUpdateInvoiceTimecardsMutation,
    useGetInvoiceTimecardsAndExpensesQuery,
    useGetInvoiceMatterSummaryQuery,
    useUpdateInvoiceMatterMutation,
    useUpdateInvoiceMatterAdjustmentsMutation,
    useUpdateInvoiceMatterCloseOnSendMutation,
    useApplyBulkDiscountMutation,
    useUpdateInvoiceMatterStatusMutation,
    useUpdateInvoiceMatterFeeArrangementTypeMutation,
    useAddInvoiceTimecardMutation,
    useAddMultiAuthorInvoiceTimecardsMutation,
    useCopyInvoiceTimecardMutation,
    useAddInvoiceExpenseMutation,
    useCopyInvoiceExpenseMutation,
    useAddNewTimecardsMutation,
    useAddNewExpensesMutation,
    useUpdateTimecardsHourlyRatesMutation,
    useDeleteInvoiceTimecardsMutation,
    useDeleteInvoiceExpensesMutation,
    useDeleteInvoiceMatterMutation,
    useGetAddableMattersQuery,
    useAddInvoiceMattersMutation,
} = APIInvoiceDetailsSlice;
